import React, { useLayoutEffect, useRef, useState } from 'react'

import { SxProps } from '@mui/material'
import Box from '@mui/material/Box'

interface DescriptionProps {
  content: string
  isLoading: boolean
  sx?: SxProps
}

export const MiniDescription: React.FC<DescriptionProps> = ({ content, isLoading, sx }) => {
  const [visibleLines, setVisibleLines] = useState<number>(0)
  const descriptionRef = useRef<HTMLDivElement>(null)

  const calculateVisibleLines = () => {
    if (descriptionRef.current) {
      const descriptionElement = descriptionRef.current
      const parentElement = descriptionElement.parentElement

      if (parentElement) {
        const parentChildren = Array.from(parentElement.children) as HTMLElement[]

        const nonDescriptionChildrenHeight = parentChildren.reduce((totalHeight, child) => {
          if (child !== descriptionElement) {
            return totalHeight + child.clientHeight
          }
          return totalHeight
        }, 0)

        const parentHeight = parentElement.clientHeight

        const availableHeight = parentHeight - nonDescriptionChildrenHeight - 24 //-24 because the height of padding is not available height for text

        const descriptionLineHeight =
          parseInt(window.getComputedStyle(descriptionElement).lineHeight, 10) + 4 //add 4 pixels because of trailing characters like p

        const calculatedVisibleLines = Math.floor(availableHeight / descriptionLineHeight)

        if (calculatedVisibleLines !== visibleLines) {
          setVisibleLines(calculatedVisibleLines)
        }
      }
    }
  }

  useLayoutEffect(() => {
    calculateVisibleLines()

    const resizeObserver = new ResizeObserver(() => calculateVisibleLines())

    const parentElement = descriptionRef.current?.parentElement
    if (parentElement) resizeObserver.observe(parentElement)

    return () => {
      if (parentElement) resizeObserver.unobserve(parentElement)
    }
  }, [content, isLoading])

  return (
    <Box
      dangerouslySetInnerHTML={{ __html: content }}
      ref={descriptionRef}
      sx={{
        display: '-webkit-box',
        fontSize: 13,
        lineHeight: '20px',
        height: visibleLines * 20 + 20, //we need to enforce the box's height so text doesnt peak out below the line clamped ellipsis line (lines * lineHeight + padding)
        m: 0,
        pb: 8, // pulse already has apadding of 8 so the 8px here and 8px in pulse will equal 16px.
        pt: 16,
        px: 8, // The new description design has a 24px padding on the x axis, however, pulse already adds 16px. In order to not break existing insights we are only using 8px here so it adds up to 24. Once all insights are moved to this repo we make pulse 0px and this 24px.
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: visibleLines,
        ...sx,
      }}
    />
  )
}
